// ── API base resolution ─────────────────────────────────────────────────────
// Every tenant workspace is served from its own origin (`rutongo.minetech.rw`)
// by a single nginx wildcard vhost that also proxies `/core/api/v1` through to
// the gateway. That makes the API same-origin, which is why the deployed builds
// set `VITE_API_BASE_URL=/core/api/v1` — a RELATIVE base.
//
// Relative is the better default for a multi-tenant SPA: one build serves every
// tenant, no CORS preflight, and the browser's own origin decides which backend
// it talks to, so the bundle can't be pinned to the wrong tenant's host. An
// absolute URL is still honoured for local development against a remote API.
const API_BASE_URL: string =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ??
  'http://localhost:4001/core/api/v1';

/** True when API_BASE_URL is same-origin (starts with `/`). */
const isRelativeApiBase = API_BASE_URL.startsWith('/');

// Payment-engine is a separate service. The API gateway exposes it under the
// `/payments/api/v1` prefix (core-service is under `/core/api/v1`). Derive the
// payment base from the core base by swapping the service segment so the same
// gateway host is reused; allow an explicit override for non-gateway setups.
const PAYMENT_API_BASE_URL: string =
  (import.meta.env['VITE_PAYMENT_API_BASE_URL'] as string | undefined) ??
  API_BASE_URL.replace('/core/api/v1', '/payments/api/v1');

// In the browser, an https page cannot open ws://|http:// sockets (mixed-content block).
// Coerce the scheme to match the page so a stale env value can't reintroduce the failure.
function resolveWsUrl(raw: string): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return raw.replace(/^http:\/\//, 'https://').replace(/^ws:\/\//, 'wss://');
  }
  return raw;
}

/**
 * Socket.IO origin.
 *
 * When the REST base is relative the gateway is same-origin, so the socket must
 * be too — nginx proxies `/socket.io` on the tenant vhost. Defaulting to
 * `localhost:4020` here would have every deployed tenant page try to open a
 * socket against the user's own machine.
 */
function resolveWsBase(): string {
  const explicit = import.meta.env['VITE_WS_URL'] as string | undefined;
  if (explicit) return resolveWsUrl(explicit);
  if (isRelativeApiBase && typeof window !== 'undefined') {
    return window.location.origin;
  }
  return resolveWsUrl('http://localhost:4020');
}

/**
 * Root domain(s) under which one leading label identifies a tenant workspace.
 * Drives the boot-time host parse and the `.minetech.rw` suffix shown in the
 * provisioning UI. Comma-separated; the first entry is the canonical one.
 */
const TENANT_ROOT_DOMAIN: string =
  (import.meta.env['VITE_TENANT_ROOT_DOMAIN'] as string | undefined) ??
  'minetech.rw';

const env = {
  API_BASE_URL,
  PAYMENT_API_BASE_URL,
  WS_URL: resolveWsBase(),
  TENANT_ROOT_DOMAIN,
  APP_NAME:
    (import.meta.env['VITE_APP_NAME'] as string | undefined) ?? 'MineTech',
  NODE_ENV: import.meta.env.MODE,
  IS_DEV: import.meta.env.DEV,
  IS_PROD: import.meta.env.PROD,
} as const;

export default env;
